import type { Omittable } from "../../lib/props";
import { clamp } from "../../lib/utils.ts";
// Pure layout math for the absolute-placement SortableGrid. Items carry an
// explicit cell rect { x, y, w, h }; this module resolves overlaps and reflows
// siblings under one of two flow disciplines:
//
//   - 'gravity' (default): gesture-local vertical compaction. The gesture's own
//     tile lands exactly where it was dropped. Every other tile holds the row it
//     already sat on and may rise only into the space the gesture vacated (so a
//     tile dragged out of a column lets the column below it close up, and a
//     delete closes the hole it leaves), never into empty space a user left on
//     purpose. Horizontal placement is preserved. Empty space is the user's:
//     the board never floats a card back to the top on its own.
//   - 'dense': document-like packing — every reflow first-fit repacks the whole
//     layout in reading order, so holes are impossible and tiles flow around
//     the active rect like text around an image.
//
// Side-effect-free, so it can be unit-tested in isolation.

export interface GridLayoutItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export type SpanConstraints = Omittable<{
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  /** Height is derived (not user-set): vertical resize handles are hidden and
   *  fit-to-content never adjusts rows. Width remains freely resizable. */
  lockH?: boolean;
  /** Content-derived rows (widget-kit `autoHeight`): the vertical handles are
   *  suppressed (rows follow the content), the side edges stay draggable for
   *  width, and the single-column stack renders the item content-sized in
   *  page flow instead of fixing its height from the row span. Pair with
   *  `lockH`. */
  autoH?: boolean;
}>;

/** A cell rect without identity — marquee selections, drop targets, and other
 *  placements that haven't minted a tile yet. */
export type GridRect = Omit<GridLayoutItem, 'id'>;

/** Reflow discipline applied on every move/resize (live previews included). */
export type GridFlow = 'gravity' | 'dense';

/** Height cap (in cells) applied when a tile's constraints carry no `maxH`.
 *  24 rows ≈ 1440px at the default 60px row — tall enough for any real widget
 *  while keeping a runaway resize drag from creating a mile-long tile. */
export const DEFAULT_MAX_H = 24;

// Pixel geometry of a rendered grid, measured at gesture start so pointer
// deltas map to whole cells. `colWidth`/`rowHeight` are the cell content
// sizes; `gapX`/`gapY` the gutters between cells.
export interface GridMetrics {
  colWidth: number;
  rowHeight: number;
  gapX: number;
  gapY: number;
}

// Map a viewport point to the top-left cell an external (palette) drag would
// insert a w×h item at, centering the span on the pointer so the ghost lands
// where the user is aiming. Returns null when the point is outside the grid's
// horizontal band or more than one row pitch above it — a small top slop keeps
// a drag skimming the very top edge previewing at y=0 instead of flashing
// away, while the horizontal rejection stays hard (the palette sits beside the
// canvas). The bottom is OPEN-ENDED: the grid element is only as tall as its
// content, but the whole canvas below it is a valid target (the caller clamps
// the row to the layout bottom, so a "below everything" drop appends after the
// last row instead of rejecting).
export function externalDropCell(
  metrics: GridMetrics,
  rect: { left: number; top: number; width: number; height: number },
  point: { x: number; y: number },
  span: { w: number; h: number },
  columns: number,
): { x: number; y: number } | null {
  if (
    point.x < rect.left ||
    point.x > rect.left + rect.width ||
    point.y < rect.top - (metrics.rowHeight + metrics.gapY)
  ) {
    return null;
  }
  const pitchX = metrics.colWidth + metrics.gapX;
  const pitchY = metrics.rowHeight + metrics.gapY;
  const x = clamp(
    Math.round((point.x - rect.left) / pitchX - span.w / 2),
    0,
    Math.max(0, columns - span.w),
  );
  const y = Math.max(0, Math.round((point.y - rect.top) / pitchY - span.h / 2));
  return { x, y };
}

/** True when any layout item covers the cell. Module-private on purpose —
 *  callers probe free space through `marqueeArea` (an occupied anchor returns
 *  null) rather than a public occupancy API. */
function cellOccupied(
  layout: GridLayoutItem[],
  x: number,
  y: number,
): boolean {
  return layout.some(
    (i) => x >= i.x && x < i.x + i.w && y >= i.y && y < i.y + i.h,
  );
}

// Snap a marquee drag (anchor cell → current pointer cell) to the largest
// obstacle-free rect the gesture can honestly claim. Expansion is dominant-
// axis-first: the axis with the larger anchor→pointer travel (ties → x)
// extends cell-by-cell along the anchor's own row/column until the first
// occupied cell; the secondary axis then extends band-by-band toward the
// pointer, stopping at the first band containing any occupied cell within the
// primary extent. The result always contains the anchor, is at least 1×1, and
// covers only free cells. Height caps at `opts.maxH` (DEFAULT_MAX_H fallback),
// truncating the edge away from the anchor. Finally the anti-island shift
// pulls a rect drawn strictly below all content up to `layoutBottom` (height
// preserved — rows at or past the bottom are free by definition), so the live
// preview shows exactly where the widget lands, the same WYSIWYG rule as the
// external phantom's append clamp. Occupied anchor → null.
export function marqueeArea(
  layout: GridLayoutItem[],
  anchor: { x: number; y: number },
  pointer: { x: number; y: number },
  columns: number,
  opts: { maxH?: number } = {},
): GridRect | null {
  // Exported from the package root, so guard the inputs hard: the expansion
  // loops below terminate via strict `!==` against the target coords — a
  // fractional or NaN coordinate would never hit equality and hang the tab.
  if (
    !Number.isInteger(anchor.x) ||
    !Number.isInteger(anchor.y) ||
    !Number.isFinite(pointer.x) ||
    !Number.isFinite(pointer.y)
  ) {
    return null;
  }
  if (anchor.x < 0 || anchor.x >= columns || anchor.y < 0) return null;
  if (cellOccupied(layout, anchor.x, anchor.y)) return null;

  const maxH = opts.maxH ?? DEFAULT_MAX_H;
  const targetX = clamp(Math.floor(pointer.x), 0, columns - 1);
  const targetY = Math.max(0, Math.floor(pointer.y));

  // Farthest free column reachable from the anchor toward targetX along row y.
  const rayX = (y: number): number => {
    const step = targetX > anchor.x ? 1 : -1;
    let x = anchor.x;
    while (x !== targetX && !cellOccupied(layout, x + step, y)) x += step;
    return x;
  };
  // Farthest free row reachable from the anchor toward targetY along column x.
  const rayY = (x: number): number => {
    const step = targetY > anchor.y ? 1 : -1;
    let y = anchor.y;
    while (y !== targetY && !cellOccupied(layout, x, y + step)) y += step;
    return y;
  };
  const rowFree = (y: number, x0: number, x1: number): boolean => {
    for (let x = x0; x <= x1; x++) {
      if (cellOccupied(layout, x, y)) return false;
    }
    return true;
  };
  const colFree = (x: number, y0: number, y1: number): boolean => {
    for (let y = y0; y <= y1; y++) {
      if (cellOccupied(layout, x, y)) return false;
    }
    return true;
  };

  let x0 = anchor.x;
  let x1 = anchor.x;
  let y0 = anchor.y;
  let y1 = anchor.y;

  if (Math.abs(pointer.x - anchor.x) >= Math.abs(pointer.y - anchor.y)) {
    // X-dominant: ray along the anchor's row, then row bands toward the pointer.
    const xEnd = rayX(anchor.y);
    x0 = Math.min(anchor.x, xEnd);
    x1 = Math.max(anchor.x, xEnd);
    const step = targetY >= anchor.y ? 1 : -1;
    let y = anchor.y;
    while (y !== targetY && y + step >= 0 && rowFree(y + step, x0, x1)) {
      y += step;
    }
    y0 = Math.min(anchor.y, y);
    y1 = Math.max(anchor.y, y);
  } else {
    // Y-dominant: ray along the anchor's column, then column bands toward the
    // pointer.
    const yEnd = rayY(anchor.x);
    y0 = Math.min(anchor.y, yEnd);
    y1 = Math.max(anchor.y, yEnd);
    const step = targetX >= anchor.x ? 1 : -1;
    let x = anchor.x;
    while (
      x !== targetX &&
      x + step >= 0 &&
      x + step < columns &&
      colFree(x + step, y0, y1)
    ) {
      x += step;
    }
    x0 = Math.min(anchor.x, x);
    x1 = Math.max(anchor.x, x);
  }

  // Height cap: truncate the edge away from the anchor (the anchor always sits
  // at one end of the vertical extent — expansion only moves toward the
  // pointer).
  if (y1 - y0 + 1 > maxH) {
    if (targetY >= anchor.y) y1 = y0 + maxH - 1;
    else y0 = y1 - maxH + 1;
  }

  const rect: GridRect = { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
  const bottom = layoutBottom(layout);
  if (rect.y > bottom) rect.y = bottom;
  return rect;
}

// Clamp a drawn rect to a widget's span constraints: w/h to span min/max
// (DEFAULT_MAX_H fallback), then x back into the grid after any width growth.
// The ONE clamp every marquee-placement store uses. NOTE: clamping can GROW
// the rect past its free area (draw 3 wide, pick a minW-6 widget) — placement
// must never append the result raw; settle it through `moveItem` so siblings
// reflow instead of overlapping.
export function clampRectToSpan(
  area: GridRect,
  constraints: SpanConstraints,
  columns: number,
): GridRect {
  const maxW = Math.min(constraints.maxW ?? columns, columns);
  const w = clamp(area.w, Math.min(constraints.minW ?? 1, maxW), Math.max(1, maxW));
  const maxH = constraints.maxH ?? DEFAULT_MAX_H;
  const h = clamp(area.h, Math.min(constraints.minH ?? 1, maxH), Math.max(1, maxH));
  const x = clamp(area.x, 0, Math.max(0, columns - w));
  return { x, y: Math.max(0, area.y), w, h };
}

function collides(a: GridLayoutItem, b: GridLayoutItem): boolean {
  if (a.id === b.id) return false;
  if (a.x + a.w <= b.x) return false;
  if (a.x >= b.x + b.w) return false;
  if (a.y + a.h <= b.y) return false;
  if (a.y >= b.y + b.h) return false;
  return true;
}

export function firstCollision(
  placed: GridLayoutItem[],
  item: GridLayoutItem,
): GridLayoutItem | undefined {
  return placed.find((p) => collides(p, item));
}

/** First row below every item — the layout's total row extent. Gesture hooks
 *  clamp drag/drop target rows to this so a downward drag can't keep extending
 *  the grid (which would feed dnd-kit's auto-scroll a forever-growing scroll
 *  range and run away). */
export function layoutBottom(items: GridLayoutItem[]): number {
  return items.reduce((max, i) => Math.max(max, i.y + i.h), 0);
}

/** The row each item may not rise above during a settle, by id. */
export type RiseCeilings = ReadonlyMap<string, number>;

export type SettleOptions = Omittable<{
  /** The gesture's own tile: placed first at its exact rect and never moved,
   *  so every other tile settles around it. */
  pinnedId?: string;
  /** Rise ceilings. An item absent from the map floats freely to row 0 —
   *  the full vertical compaction a "tidy" wants. Gesture paths pass
   *  `holdCeilings(previous, vacated)` so bystanders keep their rows. */
  ceilings?: RiseCeilings;
}>;

// Gesture-local gravity's ceilings: every item holds the row it sat on in
// `previous`, except that an item sharing columns with `vacated` may rise to
// the vacated rect's top row — the space the gesture just opened is the ONE
// hole gravity closes. Without `vacated` the settle only resolves overlaps.
export function holdCeilings(
  previous: GridLayoutItem[],
  vacated?: GridRect | null,
): RiseCeilings {
  const ceilings = new Map<string, number>();
  for (const item of previous) {
    const sharesColumns =
      vacated != null &&
      item.x < vacated.x + vacated.w &&
      vacated.x < item.x + item.w &&
      vacated.y < item.y;
    ceilings.set(item.id, sharesColumns ? vacated!.y : item.y);
  }
  return ceilings;
}

// Settle a layout: resolve every overlap downward, then let items rise. Items
// are placed top-to-bottom (then left-to-right) after the pinned one; each
// drops below anything it overlaps, then rises while the row directly above is
// clear and it is still above its ceiling (none → row 0). Earlier (higher)
// items are placed first and act as obstacles, so the input's vertical ordering
// is preserved — a caller can encode "B now sits above A" purely via y-values
// and trust the settle to keep it that way while closing the slack it allows.
export function compact(
  layout: GridLayoutItem[],
  opts: SettleOptions = {},
): GridLayoutItem[] {
  const placed: GridLayoutItem[] = [];
  const byId = new Map<string, GridLayoutItem>();

  const pinned = opts.pinnedId
    ? layout.find((i) => i.id === opts.pinnedId)
    : undefined;
  if (pinned) {
    const item: GridLayoutItem = {
      ...pinned,
      x: Math.max(0, pinned.x),
      y: Math.max(0, pinned.y),
    };
    placed.push(item);
    byId.set(item.id, item);
  }

  const sorted = layout
    .filter((i) => i.id !== pinned?.id)
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const orig of sorted) {
    const item: GridLayoutItem = {
      ...orig,
      x: Math.max(0, orig.x),
      y: Math.max(0, orig.y),
    };
    // Drop below any item already placed that we overlap...
    let hit: GridLayoutItem | undefined;
    while ((hit = firstCollision(placed, item))) {
      item.y = hit.y + hit.h;
    }
    // ...then rise while the row directly above is clear, down to the ceiling.
    // Checking one row at a time means we never tunnel through an obstacle
    // into free space above it.
    const ceiling = opts.ceilings?.get(item.id) ?? 0;
    while (
      item.y > ceiling &&
      !firstCollision(placed, { ...item, y: item.y - 1 })
    ) {
      item.y--;
    }
    placed.push(item);
    byId.set(item.id, item);
  }

  // Preserve the caller's original ordering of the array. Every id was placed
  // above, so the lookup always hits.
  return layout.map((i) => byId.get(i.id)!);
}

// Reading-order first-fit repack: every item keeps its span but is re-placed at
// the first cell (scanning top-to-bottom, left-to-right) where it fits. The
// result has no holes reachable in reading order, making it the "Tidy" action
// under gravity flow and the reflow primitive of dense flow. A packed layout is
// a fixed point (packing twice changes nothing).
//
// `opts.fixedId` pins that item at its exact rect (clamped into the grid) as an
// immovable obstacle — the dense-flow gesture path pins the active tile at its
// pointer-targeted rect and lets everything else flow around it.
export function packLayout(
  layout: GridLayoutItem[],
  columns: number,
  opts: { fixedId?: string } = {},
): GridLayoutItem[] {
  const placed: GridLayoutItem[] = [];
  const byId = new Map<string, GridLayoutItem>();

  const fixed = opts.fixedId
    ? layout.find((i) => i.id === opts.fixedId)
    : undefined;
  if (fixed) {
    const pinned: GridLayoutItem = {
      ...fixed,
      x: clamp(fixed.x, 0, Math.max(0, columns - fixed.w)),
      y: Math.max(0, fixed.y),
    };
    placed.push(pinned);
    byId.set(pinned.id, pinned);
  }

  const flowing = layout
    .filter((i) => i.id !== fixed?.id)
    .sort((a, b) => a.y - b.y || a.x - b.x);

  for (const orig of flowing) {
    // Spans are never changed; oversized items pin to x=0 and overhang.
    const maxX = Math.max(0, columns - orig.w);
    const maxY = layoutBottom(placed) + orig.h;
    let slot: { x: number; y: number } | null = null;
    for (let y = 0; y <= maxY && !slot; y++) {
      for (let x = 0; x <= maxX; x++) {
        if (!firstCollision(placed, { ...orig, x, y })) {
          slot = { x, y };
          break;
        }
      }
    }
    // The scan reaches `layoutBottom(placed)`, where nothing is placed, so a
    // free slot always exists — however oversized the item.
    const item: GridLayoutItem = { ...orig, x: slot!.x, y: slot!.y };
    placed.push(item);
    byId.set(item.id, item);
  }

  // Preserve the caller's original ordering of the array. Every id was placed
  // above, so the lookup always hits.
  return layout.map((i) => byId.get(i.id)!);
}

// Horizontal escape for a displaced item: the nearest x (ties → left) where
// the item, staying on its own rows, collides with nothing AND lands inside
// the space the active tile vacated. Requiring vacated overlap keeps the slide
// a strict trade of places with the active tile — siblings never wander into
// unrelated free space, which keeps displacement local/predictable and is what
// the nested-columns model depends on (every stack is a 1-wide column with
// free columns beside it; an unconstrained slide would fling items across
// columns instead of yielding within their stack).
function horizontalSlideX(
  work: GridLayoutItem[],
  item: GridLayoutItem,
  vacated: GridRect,
  columns: number,
): number | null {
  // No shared rows with the vacated space → no sideways trade exists.
  if (item.y >= vacated.y + vacated.h || vacated.y >= item.y + item.h) {
    return null;
  }
  let best: number | null = null;
  for (let x = 0; x <= columns - item.w; x++) {
    if (x === item.x) continue;
    if (x + item.w <= vacated.x || x >= vacated.x + vacated.w) continue;
    if (firstCollision(work, { ...item, x })) continue;
    if (best === null || Math.abs(x - item.x) < Math.abs(best - item.x)) {
      best = x;
    }
  }
  return best;
}

// Shove every item overlapping `active` out of its way, mutating `work` in
// place. Resolution favors horizontal over vertical: a displaced item first
// tries to slide along its own rows into the space the active tile vacated
// (`vacated` = the active's pre-move rect) — the sideways swap a user expects
// when dragging a tile onto a same-row neighbor. Only when no such slot exists
// does it displace vertically: on a downward drag it rises into the vacated
// space (a swap); on horizontal/upward drags it drops below. Colliders resolve
// in reading order so earlier slides become obstacles for later ones,
// independent of the caller's array order. A later compaction pass cleans up
// secondary overlaps, so the vertical branch only sets the direction.
function displaceCollisions(
  work: GridLayoutItem[],
  activeId: string,
  movedDown: boolean,
  vacated: GridRect,
  columns: number,
): void {
  const active = work.find((i) => i.id === activeId);
  if (!active) return;
  const colliding = work
    .filter((i) => i.id !== activeId && collides(active, i))
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const item of colliding) {
    const slid = horizontalSlideX(work, item, vacated, columns);
    if (slid !== null) {
      item.x = slid;
      continue;
    }
    if (movedDown) {
      const above = active.y - item.h;
      item.y = above >= 0 ? above : active.y + active.h;
    } else {
      item.y = active.y + active.h;
    }
  }
}

// Move one item to a target cell (clamped to the grid) and reflow the rest.
// Gravity: the item lands exactly where it was dropped. Whatever it lands on
// trades places horizontally when it can (slide into the vacated space along
// its own rows), otherwise gets pushed in the drag direction; then the layout
// settles with the moved item pinned and every bystander holding its row
// except to rise into the vacated space (dragging a tile down onto a sibling
// swaps them; dragging it sideways onto a same-row neighbor swaps them
// sideways; dragging it below an empty column leaves the space above it
// empty). Dense: pin the item at the target and first-fit repack everything
// else around it.
export function moveItem(
  layout: GridLayoutItem[],
  id: string,
  x: number,
  y: number,
  columns: number,
  flow: GridFlow = 'gravity',
): GridLayoutItem[] {
  const active = layout.find((i) => i.id === id);
  if (!active) return layout;

  const targetX = clamp(x, 0, Math.max(0, columns - active.w));
  const targetY = Math.max(0, y);

  const work = layout.map((i) =>
    i.id === id ? { ...i, x: targetX, y: targetY } : { ...i },
  );
  if (flow === 'dense') return packLayout(work, columns, { fixedId: id });

  const movedDown = targetY > active.y;
  displaceCollisions(work, id, movedDown, active, columns);
  return compact(work, { pinnedId: id, ceilings: holdCeilings(layout, active) });
}

// Remove one item and reflow the survivors so the hole it leaves closes.
// Gravity lets the tiles under the removed rect rise into it (and no further);
// dense first-fit repacks them in reading order. Unknown id → identity (same contract as `moveItem`), so a
// caller can feed the result straight to `diffMoves` without guarding.
export function removeItem(
  layout: GridLayoutItem[],
  id: string,
  columns: number,
  flow: GridFlow = 'gravity',
): GridLayoutItem[] {
  const removed = layout.find((i) => i.id === id);
  if (!removed) return layout;
  const rest = layout.filter((i) => i.id !== id);
  return flow === 'dense'
    ? packLayout(rest, columns)
    : compact(rest, { ceilings: holdCeilings(layout, removed) });
}

// Resize one item to a target rect (clamped to span constraints and grid width),
// then reflow siblings per the flow discipline. `x`/`y` are explicit so handles
// anchoring the opposite edge (west moves x, north moves y) can shift the
// origin, not just the span. The resized item is pinned at its rect, so a
// north-edge drag holds the dragged top edge; siblings below a shrinking tile
// rise into the rows it gave up and no further.
//
// West/north anchoring: the span clamps below can shrink the span AFTER the
// caller derived the origin from the fixed opposite edge, which would otherwise
// drag the anchored edge back in. So for a west/north drag (origin moved toward
// 0), re-derive the origin from the anchor and the CLAMPED span. East/south
// drags keep the original origin; only the span moves.
export function resizeItem(
  layout: GridLayoutItem[],
  id: string,
  x: number,
  y: number,
  w: number,
  h: number,
  columns: number,
  constraints: SpanConstraints = {},
  flow: GridFlow = 'gravity',
): GridLayoutItem[] {
  const work = layout.map((i) => {
    if (i.id !== id) return { ...i };
    let nextX = clamp(x, 0, Math.max(0, columns - 1));
    const maxW = Math.min(constraints.maxW ?? columns, columns - nextX);
    const nextW = clamp(w, constraints.minW ?? 1, Math.max(1, maxW));
    let nextY = Math.max(0, y);
    const nextH = clamp(h, constraints.minH ?? 1, constraints.maxH ?? DEFAULT_MAX_H);

    // West drag: the right edge (x + w as requested) is the anchor; hold it
    // after the span got clamped. Detected by the origin moving left of the
    // item's current x.
    if (nextX < i.x) {
      const anchorRight = x + w;
      nextX = Math.max(0, anchorRight - nextW);
    }
    // North drag: the bottom edge is the anchor; hold it after the height
    // clamped. Detected by the origin moving above the item's current y.
    if (nextY < i.y) {
      const anchorBottom = y + h;
      nextY = Math.max(0, anchorBottom - nextH);
    }

    return { ...i, x: nextX, y: nextY, w: nextW, h: nextH };
  });
  if (flow === 'dense') return packLayout(work, columns, { fixedId: id });
  const prior = layout.find((i) => i.id === id);
  return compact(work, { pinnedId: id, ceilings: holdCeilings(layout, prior) });
}

// Placement resolution for a rendered grid: an in-flight gesture preview wins;
// otherwise a live height preview (`liveSpan`, rows) folds through
// `resizeItem`; otherwise the committed layout. Keyed on the PREVIEW being
// null, NOT on "no gesture" — the yield paths null the preview while keeping
// the gesture alive, and the grid must fall through to the live span then
// (e.g. the page grid holds a yielded external gesture while a nested grid's
// preview drives the container's live growth). `liveSpan` is ignored under
// singleColumn (mobile renders committed heights only).
export function resolvePlacement(args: {
  layout: GridLayoutItem[];
  preview: GridLayoutItem[] | null;
  liveSpan: { id: string; h: number } | null;
  columns: number;
  flow: GridFlow;
  singleColumn: boolean;
  constraintsFor: (id: string) => SpanConstraints;
}): GridLayoutItem[] {
  const { layout, preview, liveSpan, columns, flow, singleColumn, constraintsFor } =
    args;
  if (preview) return preview;
  if (liveSpan && !singleColumn) {
    const entry = layout.find((i) => i.id === liveSpan.id);
    if (entry && entry.h !== liveSpan.h) {
      return resizeItem(
        layout,
        liveSpan.id,
        entry.x,
        entry.y,
        entry.w,
        liveSpan.h,
        columns,
        constraintsFor(liveSpan.id),
        flow,
      );
    }
  }
  return layout;
}

// Reconcile a gesture preview against the LIVE layout at commit time: drop
// preview entries whose id left the layout mid-gesture (a widget deleted
// mid-drag must not resurrect) and append live items the preview never saw at
// their current placement (a widget added mid-drag must not be lost).
// Identity (the same preview reference) when the two agree — the normal case.
export function reconcileLayout(
  preview: GridLayoutItem[],
  current: GridLayoutItem[],
): GridLayoutItem[] {
  const liveIds = new Set(current.map((i) => i.id));
  const kept = preview.filter((i) => liveIds.has(i.id));
  const previewIds = new Set(preview.map((i) => i.id));
  const appended = current.filter((i) => !previewIds.has(i.id));
  if (kept.length === preview.length && appended.length === 0) return preview;
  return [...kept, ...appended];
}

// Find the first free cell that fits a w×h item, scanning top-to-bottom,
// left-to-right. The insertion-time auto-placement primitive: every create path
// (add card, duplicate, palette click-to-add) calls this so new tiles fill the
// first available hole with no consumer math.
export function findFreeSlot(
  layout: GridLayoutItem[],
  w: number,
  h: number,
  columns: number,
): { x: number; y: number } {
  const width = Math.min(w, columns);
  const maxY = layoutBottom(layout) + h;
  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= columns - width; x++) {
      const probe: GridLayoutItem = { id: "__probe__", x, y, w: width, h };
      if (!firstCollision(layout, probe)) return { x, y };
    }
  }
  return { x: 0, y: layoutBottom(layout) };
}
